import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ShieldCheck, Clock, FileCheck, HardHat, BellRing, Phone } from "lucide-react";
import { SectionTag } from "@/components/Motion";
import PageSeo from "@/components/PageSeo";
import Faq from "@/components/Faq";
import PracticalGuideLinks from "@/components/PracticalGuideLinks";
import { TRADES } from "@/data/content";
import { TARIFS, formatRange, formatMonthlyRange } from "@/data/tarifs";
import { COMPANY, telHref } from "@/data/company";
import { openCallbackModal, trackEvent } from "@/lib/analytics";
import { hy } from "@/lib/hyphenate";
import { formatDateFr } from "@/lib/format";
import LATEST_POSTS from "@/data/blog/generated/latest.json";

const HERO_BASE = "https://images.unsplash.com/photo-1544164560-adac3045edb2?auto=format&fit=crop&q=60";
const HERO_IMG = `${HERO_BASE}&w=800&h=1000`;
const HERO_SRCSET = `${HERO_BASE}&w=480&h=600 480w, ${HERO_IMG} 800w`;
const CRANE_IMG =
  "https://images.unsplash.com/photo-1645488087091-9246745d90ca?auto=format&fit=crop&w=800&h=600&q=60";
const SPARKS_IMG =
  "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?auto=format&fit=crop&w=800&h=600&q=60";

const CHAPTERS = [
  {
    n: "01",
    title: "Une protection obligatoire",
    text: "Lorsque votre responsabilité décennale peut être engagée — c'est-à-dire lorsqu'un désordre peut compromettre la solidité de l'ouvrage ou le rendre impropre à sa destination — la loi impose d'être assuré avant l'ouverture des chantiers concernés. Cette responsabilité court en principe pendant dix ans à compter de la réception des travaux.",
    link: { to: "/assurance-decennale", label: "Comprendre la responsabilité décennale" },
  },
  {
    n: "02",
    title: "RC Pro et décennale, une seule démarche",
    text: "Votre dossier est étudié pour la responsabilité civile professionnelle et la garantie décennale en une seule demande. Un formulaire unique, et une couverture pensée pour l'ensemble de votre activité — avant, pendant et après les travaux.",
  },
  {
    n: "03",
    title: "Un devis en 2 minutes",
    text: "Un formulaire court, pensé pour les artisans. Vous décrivez votre entreprise, votre besoin est étudié par notre cabinet de courtage, qui vous recontacte pour préciser les possibilités adaptées à votre activité.",
  },
];

const PRODUCTS = [
  {
    to: "/devis",
    title: "RC Pro & Décennale",
    text: "Le contrat socle de tout artisan du BTP : responsabilité civile professionnelle et garantie décennale réunies.",
  },
  {
    to: "/sante",
    title: "Santé dirigeants TNS",
    text: "Une complémentaire santé pensée pour les travailleurs non salariés, avec les avantages de la loi Madelin.",
  },
  {
    to: "/sante",
    title: "Santé des salariés",
    text: "La couverture santé collective obligatoire pour vos salariés, simple à mettre en place.",
  },
  {
    to: "/prevoyance",
    title: "Prévoyance TNS",
    text: "Incapacité, invalidité, décès : maintenez vos revenus et protégez votre famille.",
  },
];

const HOME_FAQ = [
  {
    q: "L’assurance décennale est-elle obligatoire pour un artisan ?",
    a: "Oui, dès lors que vos travaux peuvent engager votre responsabilité décennale (articles 1792 et suivants du Code civil) : l’article L241-1 du Code des assurances impose d’être assuré à l’ouverture de chaque chantier concerné, y compris en micro-entreprise. Le défaut d’assurance est puni de six mois d’emprisonnement et de 75 000 € d’amende (article L243-3).",
  },
  {
    q: "Combien coûte une assurance décennale ?",
    a: "Les repères communiqués par le cabinet vont d’environ 1 000 € par an (peintre, vitrier) à 7 000 € par an (fumiste) pour un contrat RC Pro + décennale, soit environ 83 à 583 € par mois. Le tarif réel dépend de vos activités, de votre chiffre d’affaires, de votre expérience et de vos antécédents.",
  },
  {
    q: "Un auto-entrepreneur doit-il souscrire une décennale ?",
    a: "Oui, si ses travaux relèvent de la garantie décennale : le statut de micro-entrepreneur ne change pas l’obligation. Le chiffre d’affaires, souvent plus faible au démarrage, fait partie des critères de tarification étudiés par l’assureur.",
  },
  {
    q: "Peut-on être assuré en création d’entreprise ?",
    a: "Une étude est possible : l’assureur examine votre expérience, vos diplômes, vos activités et votre chiffre d’affaires prévisionnel. L’acceptation n’est pas automatique ; préparez vos justificatifs avant la date de votre premier chantier.",
  },
  {
    q: "Que faire si les assureurs refusent de m’assurer ?",
    a: "Adressez vos demandes par lettre recommandée avec avis de réception. En cas de refus, vous pouvez saisir le Bureau central de tarification dans les 15 jours : il fixe la prime moyennant laquelle l’assureur sollicité est tenu de vous garantir.",
  },
  {
    q: "Combien de temps pour obtenir l’attestation décennale ?",
    a: "Le délai dépend de l’étude du dossier et des pièces fournies. L’attestation est délivrée par l’assureur après acceptation et prise d’effet du contrat : indiquez la date de votre prochain chantier dans votre demande de devis.",
  },
];

const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: HOME_FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
};

export default function Home() {
  return (
    <div data-testid="home-page">
      <PageSeo path="/" jsonLd={FAQ_LD} />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-ink/15">
        <div className="mx-auto grid max-w-[1440px] lg:grid-cols-12">
          <div className="lg:col-span-8 px-5 sm:px-8 pt-14 pb-12 lg:pt-24 lg:pb-20 flex flex-col justify-center">
            <SectionTag className="mb-8">RC Pro + Décennale — Artisans du BTP — Devis gratuit</SectionTag>
            <h1 className="font-display font-extrabold uppercase tracking-tighter leading-[0.98] text-[8.2vw] lg:text-[min(calc(6.3vw_-_6px),5.2rem)]">
              <span className="block">Assurance</span>
              <span className="block text-accent">décennale</span>
              <span className="block text-outline-ink">artisan.</span>
            </h1>
            <p className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-ink/80">
              Courtage en RC Pro et assurance décennale pour les artisans du bâtiment.
              Décrivez vos travaux : notre cabinet étudie votre situation
              pour rechercher un contrat adapté. Demande gratuite et sans engagement.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/devis"
                data-testid="hero-cta-devis"
                className="inline-flex items-center gap-3 border-2 border-ink bg-accent px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper shadow-brutal transition-all hover:bg-ink hover:shadow-none"
              >
                Obtenir mon devis
                <ArrowRight className="h-4 w-4" />
              </Link>
              <button
                onClick={openCallbackModal}
                data-testid="hero-cta-callback"
                className="inline-flex items-center gap-2 border-2 border-ink px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:bg-ink hover:text-paper"
              >
                <BellRing className="h-4 w-4" />
                Être rappelé
              </button>
              {COMPANY.phone && (
                <a
                  href={telHref(COMPANY.phone)}
                  onClick={() => trackEvent("phone_click", { position: "hero" })}
                  data-testid="hero-cta-phone"
                  className="inline-flex items-center gap-2 border-2 border-ink bg-ink px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors hover:bg-accent hover:border-accent"
                >
                  <Phone className="h-4 w-4" />
                  {COMPANY.phone}
                </a>
              )}
            </div>
            <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
              {["22 métiers du bâtiment", "Rappel rapide par un spécialiste BTP", "Devis gratuit & sans engagement"].map((t) => (
                <li key={t} data-testid="hero-trust-item" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-ink/70">
                  <ShieldCheck className="h-4 w-4 text-accent" aria-hidden="true" />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4 relative border-t lg:border-t-0 lg:border-l border-ink/15">
            <div className="h-[320px] lg:h-full min-h-[320px] overflow-hidden">
              <img
                src={HERO_IMG}
                srcSet={HERO_SRCSET}
                sizes="(min-width: 1024px) 33vw, 100vw"
                alt="Charpentier réalisant une découpe de précision"
                className="img-brutal h-full w-full object-cover"
                width="800"
                height="1000"
                decoding="async"
              />
            </div>
            <div className="absolute bottom-0 left-0 border-t border-r border-ink/15 bg-paper px-5 py-3 font-mono text-[10px] uppercase tracking-[0.25em]">
              Fig. 01 — Le geste précis
            </div>
          </div>
        </div>

        <div className="border-t border-ink/15">
          <div className="mx-auto grid max-w-[1440px] grid-cols-3 divide-x divide-ink/15">
            {[
              ["02 min", "Devis express"],
              ["22", "Métiers couverts"],
              ["Sur mesure", "Étude de votre activité"],
            ].map(([big, small]) => (
              <div key={small} className="px-4 sm:px-8 py-6 text-center sm:text-left">
                <p className="font-display text-2xl sm:text-4xl font-extrabold tracking-tighter">{big}</p>
                <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-ink/65 mt-1">{small}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRIX PAR MÉTIER */}
      <section className="mx-auto max-w-[1440px] px-5 sm:px-8 py-20 lg:py-28" data-testid="metiers-preview" aria-labelledby="prix-metiers-title">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-6">
          <div>
            <SectionTag className="mb-4">Prix indicatifs 2026</SectionTag>
            <h2 id="prix-metiers-title" className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tighter leading-none">
              Prix de la décennale<br />par métier.
            </h2>
          </div>
          <Link
            to="/prix"
            data-testid="metiers-preview-link"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] border-b-2 border-accent pb-1 hover:text-accent transition-colors"
          >
            Estimer mon prix
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
        <p className="max-w-3xl text-base leading-relaxed text-ink/75 mb-10">
          Repères de prime annuelle communiqués par le cabinet pour un contrat RC Pro + décennale, avec leur
          équivalent mensuel. Chaque fiche détaille les travaux à déclarer, les pièges des offres « pas chères »
          et les documents à préparer.
        </p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-ink/15">
          {TRADES.map((t, i) => (
            <li key={t.slug} className="border-b border-r border-ink/15">
              <Link
                to={`/metiers/${t.slug}`}
                data-testid={`metier-card-${i}`}
                className="group flex h-full flex-col justify-between gap-4 p-5 min-h-[128px] transition-colors hover:bg-ink hover:text-paper"
              >
                <span className="flex items-start justify-between gap-3">
                  <span className="font-display text-base font-bold uppercase tracking-tight leading-tight">{t.name}</span>
                  <HardHat className="h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
                </span>
                <span>
                  <span className="block font-mono text-sm font-medium">{formatRange(TARIFS[t.slug])} / an</span>
                  <span className="block text-xs text-ink/60 group-hover:text-paper/70">≈ {formatMonthlyRange(TARIFS[t.slug])} / mois</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-xs leading-relaxed text-ink/60 max-w-3xl">
          Montants indicatifs, hors garantie de tarif : la prime réelle dépend notamment du chiffre d’affaires, de l’expérience,
          des activités déclarées, des antécédents et des franchises retenues.
        </p>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="mx-auto max-w-[1440px] px-5 sm:px-8 pb-20 lg:pb-28" data-testid="manifesto-section">
        <SectionTag className="mb-4">Le manifeste</SectionTag>
        <h2 className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tighter leading-none mb-16">
          {hy("Construire, c'est")}<br />déjà <span className="text-accent">assurer.</span>
        </h2>
        <div>
          {CHAPTERS.map((c) => (
            <div key={c.n} className="grid gap-4 border-t-2 border-ink py-10 sm:grid-cols-12 sm:gap-8 items-start">
              <p className="font-display text-5xl sm:text-6xl font-extrabold tracking-tighter text-accent sm:col-span-2">
                {c.n}
              </p>
              <h3 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tighter sm:col-span-4">
                {hy(c.title)}
              </h3>
              <div className="text-base leading-relaxed text-ink/75 sm:col-span-6">
                <p>{c.text}</p>
                {c.link && (
                  <Link
                    to={c.link.to}
                    className="mt-3 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] border-b-2 border-accent pb-1 hover:text-accent transition-colors"
                    data-testid="manifesto-link-decennale"
                  >
                    {c.link.label}
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* IMAGE BREAK */}
      <section className="border-y border-ink/15" aria-hidden="true">
        <div className="mx-auto grid max-w-[1440px] lg:grid-cols-2">
          <div className="h-[280px] lg:h-[380px] overflow-hidden border-b lg:border-b-0 lg:border-r border-ink/15">
            <img src={CRANE_IMG} alt="" className="img-brutal h-full w-full object-cover" width="800" height="600" loading="lazy" decoding="async" />
          </div>
          <div className="h-[280px] lg:h-[380px] overflow-hidden">
            <img src={SPARKS_IMG} alt="" className="img-brutal h-full w-full object-cover" width="800" height="600" loading="lazy" decoding="async" />
          </div>
        </div>
      </section>

      {/* PRODUITS */}
      <section className="bg-night text-paper" data-testid="products-section">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 py-20 lg:py-28">
          <SectionTag className="mb-4 !text-paper/60">Nos garanties</SectionTag>
          <h2 className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tighter leading-none mb-16">
            Un bouclier complet<br />pour votre <span className="text-accent">entreprise.</span>
          </h2>
          <div className="grid gap-px bg-paper/15 border border-paper/15 sm:grid-cols-2">
            {PRODUCTS.map((p, i) => (
              <div key={p.title} className="bg-night">
                <Link
                  to={p.to}
                  data-testid={`product-card-${i}`}
                  className="group flex h-full flex-col justify-between p-8 min-h-[220px] transition-colors hover:bg-accent"
                >
                  <div className="flex items-start justify-between">
                    <p className="font-mono text-[11px] tracking-[0.25em] text-paper/55 group-hover:text-paper">
                      {String(i + 1).padStart(2, "0")}
                    </p>
                    <ArrowUpRight className="h-6 w-6 text-paper/50 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-paper" />
                  </div>
                  <div>
                    <h3 className="font-display text-2xl font-bold uppercase tracking-tighter mb-2">{p.title}</h3>
                    <p className="text-sm leading-relaxed text-paper/70 group-hover:text-paper">{p.text}</p>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REASSURANCE */}
      <section className="border-b border-ink/15" data-testid="trust-section">
        <div className="mx-auto grid max-w-[1440px] sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 lg:divide-x divide-ink/15">
          {[
            [FileCheck, "Devis gratuit", "Sans frais cachés, sans engagement de souscription."],
            [Clock, "Réponse rapide", "Un conseiller spécialiste BTP vous recontacte — objectif : sous 24h ouvrées."],
            [ShieldCheck, "Étude de votre activité", "Notre cabinet étudie votre activité et vos besoins de couverture."],
            [HardHat, "Création étudiée", "Société en création ou sans décennale depuis plusieurs mois ? Votre dossier est étudié."],
          ].map(([Icon, title, text]) => (
            <div key={title} className="flex h-full flex-col gap-4 p-8">
              <Icon className="h-6 w-6 text-accent" aria-hidden="true" />
              <h3 className="font-display text-lg font-bold uppercase tracking-tight">{title}</h3>
              <p className="text-sm leading-relaxed text-ink/75">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 py-8">
        <PracticalGuideLinks limit={6} title="Guides pratiques : vos questions, nos réponses" />
        <Link to="/guide" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] border-b-2 border-accent pb-1 hover:text-accent transition-colors">
          Tous les guides décennale
          <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>

      {LATEST_POSTS.length > 0 && (
        <section className="mx-auto max-w-[1440px] px-5 sm:px-8 py-12" aria-labelledby="home-blog-title" data-testid="home-blog">
          <SectionTag className="mb-4">Blog</SectionTag>
          <h2 id="home-blog-title" className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tighter leading-none mb-8">
            Actualités de<br />{hy("l’assurance")} <span className="text-accent">BTP.</span>
          </h2>
          <ul className="grid gap-4 md:grid-cols-3">
            {LATEST_POSTS.map((post) => (
              <li key={post.slug}>
                <Link to={`/blog/${post.slug}`} className="group flex h-full flex-col border-2 border-ink bg-white p-6 hover:border-accent">
                  <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/65">
                    <span className="text-accent">{post.category}</span> · {formatDateFr(post.date)}
                  </span>
                  <span className="mt-3 font-display text-xl font-bold group-hover:text-accent">{post.h1}</span>
                  <span className="mt-3 text-sm leading-relaxed text-ink/75">{post.excerpt}</span>
                  <span className="mt-auto pt-4 flex items-center gap-2 text-sm font-bold">Lire l’article <ArrowUpRight aria-hidden="true" className="h-4 w-4" /></span>
                </Link>
              </li>
            ))}
          </ul>
          <Link to="/blog" className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] border-b-2 border-accent pb-1 hover:text-accent transition-colors">
            Tous les articles
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </section>
      )}

      {/* FAQ */}
      <section className="mx-auto max-w-[1440px] px-5 sm:px-8 py-16 lg:py-24" aria-labelledby="home-faq-title">
        <SectionTag className="mb-4">Questions fréquentes</SectionTag>
        <h2 id="home-faq-title" className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tighter leading-none mb-10">
          Assurance décennale :<br />l’essentiel.
        </h2>
        <Faq items={HOME_FAQ} prefix="home-faq" />
      </section>

      {/* CTA BAND */}
      <section className="bg-accent text-paper" data-testid="cta-band">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 py-20 lg:py-28">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-paper mb-6">
            Chantier suivant
          </p>
          <h2 className="font-display text-[11vw] sm:text-7xl lg:text-8xl font-extrabold uppercase tracking-tighter leading-[0.9]">
            Votre devis<br />en 2 minutes.
          </h2>
          <Link
            to="/devis"
            data-testid="cta-band-devis"
            className="mt-12 inline-flex items-center gap-3 border-2 border-night bg-night px-10 py-5 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors hover:bg-transparent hover:text-night"
          >
            Demander un devis
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
